import React, { useEffect, useState } from 'react';
import { X, RefreshCw, BellRing, Send, RotateCcw, CalendarClock, Trash2 } from 'lucide-react';
import followupService from '../../services/global/followupService';

// Rótulos amigáveis para a situação calculada pelo backend (services/desafio_participante.py)
export const SITUACAO = {
  em_dia: { label: 'Em dia', color: '#22c55e' },
  em_espera: { label: 'Parado (sem resposta)', color: '#f59e0b' },
  pendente: { label: 'Reenvio pendente', color: '#3b82f6' },
  falha: { label: 'Falha de envio', color: '#ef4444' },
  concluido: { label: 'Concluído', color: '#64748b' },
  aguardando_inicio: { label: 'Começa em breve', color: '#64748b' },
  sem_data: { label: 'Sem data', color: '#64748b' },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-');

const RecipientDetailModal = ({ isOpen, phone, history, onClose, colors, followupId, onResend, situacao, onChanged, onRemove, totalDays }) => {
  const [resendingDay, setResendingDay] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [diaAlvo, setDiaAlvo] = useState('');
  const [agente, setAgente] = useState(null);

  useEffect(() => {
    setMsg(null);
    setAgente(null);
    if (!isOpen || !phone || !followupId) return;
    followupService.situacaoParticipante(followupId, phone)
      .then((r) => setAgente(r?.data?.agente || null))
      .catch(() => setAgente(null));
  }, [isOpen, phone, followupId]);

  if (!isOpen) return null;

  const recipientHistory = history
    .filter(h => h.recipient === phone)
    .sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      return new Date(a.sent_at) - new Date(b.sent_at);
    });

  // Consolidate by day
  const dayMap = {};
  recipientHistory.forEach(h => {
    if (!dayMap[h.day]) {
      dayMap[h.day] = { day: h.day, total: 0, sent: 0, failed: 0, pending: 0, lastDate: h.sent_at, isTest: h.is_test };
    }
    dayMap[h.day].total++;
    if (h.status === 'sent') dayMap[h.day].sent++;
    else dayMap[h.day].failed++;
    if (h.retry_status === 'pending') dayMap[h.day].pending++;
    if (new Date(h.sent_at) > new Date(dayMap[h.day].lastDate)) {
      dayMap[h.day].lastDate = h.sent_at;
    }
  });

  const dayRows = Object.values(dayMap).sort((a, b) => a.day - b.day);
  const sit = situacao || {};
  const sitCfg = SITUACAO[sit.status] || { label: sit.status || '-', color: colors.textSecondary };
  const topo = agente?.followups?.[0];

  const run = async (key, fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(key); setMsg(null);
    try {
      const r = await fn();
      const d = r?.data || r || {};
      let texto = 'Feito.';
      if (key === 'lembrete') texto = d.ok ? `Lembrete enviado (template ${d.template}).` : `Não foi possível enviar: ${d.error || 'erro'}`;
      if (key === 'pendentes') texto = `${d.sent || 0} material(is) entregue(s), ${d.failed || 0} falha(s), ${d.skipped || 0} adiado(s). ` +
        ((d.sent || 0) === 0 ? 'Se a pessoa não falou com o número nas últimas 24h, a Meta não deixa entregar: use "Enviar lembrete".' : '');
      if (key === 'reiniciar') texto = `Reiniciado: hoje é o dia 1 (${d.registros_apagados || 0} registros antigos apagados). O material sai no próximo ciclo.`;
      if (key === 'dia') texto = `Pronto: hoje é o dia ${d.dia}. O material desse dia sai no próximo ciclo (${d.registros_apagados || 0} registros apagados).`;
      setMsg({ ok: true, text: texto });
      if (onChanged) await onChanged();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message || 'Erro.' });
    } finally {
      setBusy(null);
    }
  };

  const btn = (extra = {}) => ({
    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent', color: colors.textPrimary, cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1, ...extra,
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '640px', maxHeight: '86vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              Participante
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: colors.textSecondary, fontFamily: 'monospace' }}>
              {phone}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Situação */}
          <div style={{ padding: 12, borderRadius: 10, backgroundColor: colors.bgSecondary, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700, backgroundColor: `${sitCfg.color}20`, color: sitCfg.color }}>
                {sitCfg.label}
              </span>
              <span style={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                {sit.dia_atual ? `Dia ${sit.dia_atual} de ${sit.total_days || totalDays || '?'}` : 'Sem dia'}
                {topo && topo.dia_rotulado != null && topo.dia_rotulado !== sit.dia_atual ? ` · na conversa: "Dia ${topo.dia_rotulado}"` : ''}
              </span>
              {sit.dias_segurados > 0 && (
                <span style={{ fontSize: 12, color: colors.textSecondary }}>({sit.dias_segurados} dia(s) segurado(s) por falta de resposta)</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6, lineHeight: 1.5 }}>
              {sit.proximo_passo || 'Carregando…'}
              {sit.espera_desde ? ` Parado desde ${fmtDate(sit.espera_desde)}.` : ''}
              {sit.ultimo_envio_em ? ` Último envio: ${fmtDate(sit.ultimo_envio_em)}.` : ''}
            </div>
            {topo?.tema_do_dia && (
              <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>Tema de hoje: {topo.tema_do_dia}</div>
            )}
          </div>

          {/* Ações de manutenção */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <button
              disabled={!!busy}
              title="Manda o template que pede 'responda SIM'. Use quando a pessoa diz que não recebeu nada."
              onClick={() => run('lembrete', () => followupService.enviarLembrete(followupId, phone),
                `Enviar agora o lembrete de abertura (template) para ${phone}?`)}
              style={btn()}
            >
              <BellRing size={13} /> Enviar lembrete
            </button>
            <button
              disabled={!!busy}
              title="Tenta entregar o material pendente agora (só funciona se a pessoa falou com o número nas últimas 24h)."
              onClick={() => run('pendentes', () => followupService.entregarPendentes(followupId, phone))}
              style={btn()}
            >
              <Send size={13} /> Entregar pendentes
            </button>
            <button
              disabled={!!busy}
              title="Apaga o histórico desta pessoa neste follow-up e recomeça do dia 1 hoje."
              onClick={() => run('reiniciar', () => followupService.reiniciarParticipante(followupId, phone),
                `Recomeçar ${phone} do DIA 1 hoje?\n\nO histórico de envio dela neste follow-up será apagado e o agente esquece a rodada antiga. O material do dia 1 sai no próximo ciclo (a cada 3 min, respeitando o horário configurado).`)}
              style={btn({ borderColor: '#f59e0b60', color: '#b45309' })}
            >
              <RotateCcw size={13} /> Reiniciar do dia 1
            </button>
            {onRemove && (
              <button
                disabled={!!busy}
                title="Tira o número do follow-up (para de receber). O histórico fica guardado."
                onClick={() => { if (window.confirm(`Remover ${phone} do follow-up? A pessoa para de receber o desafio.`)) onRemove(phone); }}
                style={btn({ borderColor: '#ef444460', color: '#ef4444' })}
              >
                <Trash2 size={13} /> Remover
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
            <CalendarClock size={14} color={colors.textSecondary} />
            <span style={{ fontSize: 12, color: colors.textSecondary }}>Colocar hoje no dia</span>
            <input
              value={diaAlvo}
              onChange={(e) => setDiaAlvo(e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder={sit.dia_atual ? String(sit.dia_atual) : '1'}
              style={{ width: 52, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: 13 }}
            />
            <button
              disabled={!!busy || !diaAlvo}
              onClick={() => run('dia', () => followupService.definirDiaParticipante(followupId, phone, Number(diaAlvo)),
                `Colocar ${phone} no dia ${diaAlvo} HOJE?\n\nO que já foi entregue antes do dia ${diaAlvo} fica; do dia ${diaAlvo} em diante o material volta a ser enviado, um dia por dia.`)}
              style={btn({ opacity: busy || !diaAlvo ? 0.5 : 1 })}
            >
              Aplicar
            </button>
          </div>
          {msg && (
            <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 8, fontSize: 12, backgroundColor: msg.ok ? '#22c55e15' : '#ef444415', color: msg.ok ? '#15803d' : '#b91c1c' }}>
              {msg.text}
            </div>
          )}

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '10px',
              backgroundColor: colors.bgSecondary, textAlign: 'center',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: colors.accentPrimary }}>
                {dayRows.length}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>Dias com envio</div>
            </div>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '10px',
              backgroundColor: colors.bgSecondary, textAlign: 'center',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
                {recipientHistory.filter(h => h.status === 'sent').length}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>Msgs entregues</div>
            </div>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '10px',
              backgroundColor: colors.bgSecondary, textAlign: 'center',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: recipientHistory.some(h => h.status !== 'sent') ? '#ef4444' : '#22c55e' }}>
                {recipientHistory.filter(h => h.status !== 'sent').length}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>Falhas / pendentes</div>
            </div>
          </div>

          {/* History table */}
          {dayRows.length === 0 ? (
            <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '20px 0' }}>
              Nenhum envio registrado.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Dia</th>
                  <th style={{ textAlign: 'center', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Msgs</th>
                  <th style={{ textAlign: 'center', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Data</th>
                  {onResend && <th style={{ textAlign: 'center', padding: '8px', color: colors.textSecondary, fontWeight: 500, width: '50px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {dayRows.map(row => {
                  const allSent = row.failed === 0;
                  const allFailed = row.sent === 0;
                  const hasFailed = row.failed > 0;
                  const statusLabel = allSent ? 'Entregue' : row.pending > 0 ? (allFailed ? 'Aguardando resposta' : 'Parcial (pendente)') : allFailed ? 'Falhou' : 'Parcial';
                  const statusColor = allSent ? '#22c55e' : row.pending > 0 ? '#f59e0b' : allFailed ? '#ef4444' : '#f59e0b';
                  return (
                    <tr key={row.day} style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                      <td style={{ padding: '8px', color: colors.textPrimary, fontWeight: 500 }}>
                        Dia {row.day}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', color: colors.textSecondary }}>
                        {row.sent}/{row.total}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                          backgroundColor: `${statusColor}20`, color: statusColor,
                        }}>
                          {statusLabel}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', color: colors.textSecondary }}>
                        {new Date(row.lastDate).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      {onResend && (
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          {hasFailed && (
                            <button
                              onClick={async () => {
                                setResendingDay(row.day);
                                try {
                                  await onResend(row.day);
                                } finally {
                                  setResendingDay(null);
                                }
                              }}
                              disabled={resendingDay === row.day}
                              title="Reenviar este dia"
                              style={{
                                background: 'none', border: 'none', cursor: resendingDay === row.day ? 'wait' : 'pointer',
                                color: colors.accentPrimary, padding: '4px', borderRadius: '6px',
                                opacity: resendingDay === row.day ? 0.5 : 1,
                              }}
                            >
                              <RefreshCw size={14} style={resendingDay === row.day ? { animation: 'spin 1s linear infinite' } : {}} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipientDetailModal;
